import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { serverDetailData } from '@/mocks/serverDetail';

const cabinetFeatures = [
  {
    title: '超大容量',
    icon: 'ri-database-2-line',
    items: [
      '高度按需定制，单侧最多可容纳400个第二代U盾',
      '或容纳800个第一代U盾',
      '满足大型企业与集团财务中心海量U盾管理需求',
    ],
  },
  {
    title: '多重开锁',
    icon: 'ri-lock-unlock-line',
    items: [
      '指纹认证开锁，精准识别',
      '密码保护开锁，安全可靠',
      '可选组合方式：（指纹 且 密码）+（指纹 或 密码）',
    ],
  },
  {
    title: '精密结构',
    icon: 'ri-settings-3-line',
    items: [
      '柜内四只立柱，立柱标有U数方便安装',
      '高端工艺卡扣式设计，采用牙文设计安装省力',
      '可视内里设计，U盾状态一目了然',
    ],
  },
  {
    title: '优质材质',
    icon: 'ri-hammer-line',
    items: [
      '优质冷轧钢板，结构坚固耐用',
      '精准面板切割，做工精细',
      '高端喷涂工艺，防腐防锈',
    ],
  },
  {
    title: '散热通风',
    icon: 'ri-windy-line',
    items: [
      '柜顶配风扇，有效降温，排灰除尘',
      '可视通风窗设计，内置隔板',
      '优化风道设计，确保设备长期稳定运行',
    ],
  },
  {
    title: '便捷移动',
    icon: 'ri-drag-move-line',
    items: [
      '底部配置移动轮，方便机房内灵活移位',
      '平衡支脚设计，静载安全使用',
      '提升稳定性能，防止意外倾倒',
    ],
  },
];

export default function ModelCXDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(0);

  const cx = serverDetailData.models[6];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <nav className="mb-8 flex items-center gap-2 text-sm text-foreground-300">
              <Link to="/" className="hover:text-primary-400 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to="/product-center"
                className="hover:text-primary-400 transition-colors"
              >
                产品中心
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/product-center/${slug}`}
                className="hover:text-primary-400 transition-colors"
              >
                网银U盾服务器
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-100 font-medium">{cx.name}</span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="max-w-3xl">
                <span className="text-xs font-medium text-primary-400 tracking-wide font-label">
                  {cx.subtitle}
                </span>
                <h1 className="mt-3 text-3xl md:text-5xl font-black text-foreground-50 font-heading">
                  {cx.name}
                </h1>
                <p className="mt-5 text-base md:text-lg text-foreground-300 leading-relaxed max-w-2xl">
                  专为U盾服务器设计的指纹密码锁机柜，支持多种开锁方式，大容量设计满足企业级U盾集中管理需求。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 产品详情主区：左大图 + 右信息 */}
        <section className="pb-16 md:pb-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              {/* 左侧大图 */}
              <div className="rounded-xl overflow-hidden border border-background-200/50 bg-background-100 flex items-center justify-center">
                <img
                  src={cx.image}
                  alt={cx.name}
                  title={`${cx.name} 指纹密码锁机柜`}
                  className="w-full h-72 md:h-96 object-contain"
                />
              </div>

              {/* 右侧信息 */}
              <div className="flex flex-col">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-3">
                  产品介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                  {cx.intro}
                </p>

                {/* 规格亮点标签 */}
                <div className="mt-5 flex flex-wrap gap-2">
                  {cx.features.map((f) => (
                    <span
                      key={f}
                      className="px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-500 text-xs font-medium whitespace-nowrap"
                    >
                      {f}
                    </span>
                  ))}
                </div>

                {/* 开锁方式展示 */}
                <div className="mt-6 p-4 rounded-lg bg-background-100 border border-background-200/50">
                  <h3 className="text-sm font-bold text-foreground-50 mb-3">可选开锁方式</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-50 border border-background-200/40">
                      <i className="ri-fingerprint-line text-primary-500" />
                      <span className="text-sm text-foreground-300">指纹认证</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-50 border border-background-200/40">
                      <i className="ri-lock-password-line text-primary-500" />
                      <span className="text-sm text-foreground-300">密码保护</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-50 border border-background-200/40">
                      <i className="ri-shield-keyhole-line text-primary-500" />
                      <span className="text-sm text-foreground-300">指纹+密码</span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to={`/product-center/${slug}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-300 text-sm font-semibold rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line" />
                    返回产品列表
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 机柜特性参数 */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                机柜特性参数
              </h2>
              <p className="mt-2 text-sm text-foreground-300">
                以实际供货产品功能与指标为准，产品升级请联系销售顾问获取最新资料
              </p>
            </div>

            {/* Tab 切换 */}
            <div className="flex flex-wrap gap-2 mb-6">
              {cabinetFeatures.map((cat, idx) => (
                <button
                  key={cat.title}
                  type="button"
                  onClick={() => setActiveTab(idx)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeTab === idx
                      ? 'bg-primary-500 text-foreground-50'
                      : 'bg-background-50 text-foreground-300 hover:bg-background-200/60'
                  }`}
                >
                  {cat.title}
                </button>
              ))}
            </div>

            {/* Tab 内容 */}
            <div className="bg-background-50 rounded-xl border border-background-200/50 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                  <i className={`${cabinetFeatures[activeTab].icon} text-xl`} />
                </div>
                <h3 className="text-base md:text-lg font-bold text-foreground-50 font-heading">
                  {cabinetFeatures[activeTab].title}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {cabinetFeatures[activeTab].items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/40"
                  >
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs" />
                    </div>
                    <span className="text-sm text-foreground-300 leading-relaxed">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}